import React, { useState, useEffect, FormEvent, useMemo } from 'react';
import { Calendar, MessageSquare, Bell, ChevronRight, Mail, Menu, X, User, Building2, Shield, MapPin, Target, Wine, Globe, Bot, Users, TrendingUp } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Logo } from './components/Logo';
import { SlideIn } from './components/SlideIn';

function App() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [activeDemo, setActiveDemo] = useState<string | null>(null);
  const [scrollY, setScrollY] = useState(0);
  const [cursorPosition, setCursorPosition] = useState({ x: 0, y: 0 });
  const [conversationStep, setConversationStep] = useState(0);
  const [formStatus, setFormStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');
  const [titleIndex, setTitleIndex] = useState(0);

  const titles = useMemo(() => [
    "Meer Leads.",
    "Meer Klanten.",
    "Meer Omzet."
  ], []);

  useEffect(() => {
    const interval = setInterval(() => {
      setTitleIndex((prev) => (prev + 1) % titles.length);
    }, 3000);
    return () => clearInterval(interval);
  }, [titles]);

  useEffect(() => {
    const handleScroll = () => {
      setScrollY(window.scrollY);
    };

    const handleMouseMove = (e: MouseEvent) => {
      setCursorPosition({ x: e.clientX, y: e.clientY });
    };

    window.addEventListener('scroll', handleScroll);
    window.addEventListener('mousemove', handleMouseMove);
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('mousemove', handleMouseMove);
    };
  }, []);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    setFormStatus('sending');

    try {
      const response = await fetch('https://hook.eu2.make.com/rtf2vm2qpdl26l8m8nr4sprfpfpfjrsn', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: form.from_name.value,
          email: form.from_email.value,
          message: form.message.value,
          to_email: 'jason@jkautomate.com'
        }),
      });

      if (!response.ok) throw new Error('Network response was not ok');
      
      form.reset();
      setFormStatus('success');
      setTimeout(() => setFormStatus('idle'), 3000);
    } catch (error) {
      console.error('Error sending form:', error);
      setFormStatus('error');
      setTimeout(() => setFormStatus('idle'), 3000);
    }
  };

  const reservationConversation = [
    { text: "Hallo! Welkom bij Restaurant Naam 🍽️. Wil je een tafel reserveren? (Ja/Nee)", isAi: true },
    { text: "Ja", isAi: false },
    { text: "Geweldig! Wat is je naam?", isAi: true },
    { text: "Jan de Vries", isAi: false },
    { text: "Voor welke datum wil je reserveren?", isAi: true },
    { text: "Vrijdag 29 maart", isAi: false },
    { text: "Hoe laat wil je komen?", isAi: true },
    { text: "19:30", isAi: false },
    { text: "Voor hoeveel personen is de reservering?", isAi: true },
    { text: "Voor 2 personen.", isAi: false },
    { text: "Even controleren of we op 29 maart om 19:30 plek hebben voor 2 personen... ⏳", isAi: true },
    { text: "Goed nieuws! We hebben een tafel voor je op 29 maart om 19:30. Wil je de reservering bevestigen? (Ja/Nee)", isAi: true },
    { text: "Ja", isAi: false },
    { text: "Top! Je reservering is bevestigd. 🎉", isAi: true },
  ];

  const wineConversation = [
    { text: "Wil je alvast een fles wijn of een ander drankje bestellen voor bij aankomst? 🍷🍾 (Ja/Nee)", isAi: true },
    { text: "Ja, welke wijnen hebben jullie?", isAi: false },
    { text: "We hebben de volgende wijnen beschikbaar:\n1️⃣ Rode wijn: Merlot (€29,95)\n2️⃣ Witte wijn: Chardonnay (€27,95)\n3️⃣ Mousserend: Prosecco (€34,95)\n\nWil je een keuze maken? Type het nummer van je keuze.", isAi: true },
    { text: "1", isAi: false },
    { text: "Super keuze! We zetten een fles Merlot voor je klaar bij aankomst. 🍷 Wil je nog iets anders toevoegen? (Ja/Nee)", isAi: true },
    { text: "Nee, dat is alles.", isAi: false },
    { text: "Perfect! We zien je op 29 maart om 19:30 bij Restaurant Naam. Je hebt gereserveerd voor 2 personen en een fles Merlot staat klaar. Tot dan! 🍽️🍷", isAi: true },
  ];

  useEffect(() => {
    if (activeDemo === 'booking') {
      const interval = setInterval(() => {
        setConversationStep(prev => {
          const maxSteps = reservationConversation.length + wineConversation.length;
          return prev < maxSteps ? prev + 1 : prev;
        });
      }, 1000);
      return () => clearInterval(interval);
    }
  }, [activeDemo]);

  return (
    <div className="min-h-screen text-cyan-400">
      <div 
        id="custom-cursor"
        style={{
          left: `${cursorPosition.x - 6}px`,
          top: `${cursorPosition.y - 6}px`
        }}
      />
      <nav className="fixed w-full retro-nav z-50">
        <div className="container mx-auto px-4 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-2">
              <Logo className="w-8 h-8 text-cyan-400" />
              <span className="text-xl retro-terminal">AUTOMATE</span>
            </div>
            
            <div className="hidden md:flex space-x-8 retro-terminal">
              <NavLink href="#over-mij">OVER MIJ</NavLink>
              <NavLink href="#features">FEATURES</NavLink>
              <NavLink href="#demo">DEMO</NavLink>
              <NavLink href="#contact">CONTACT</NavLink>
            </div>

            <button 
              className="md:hidden"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
            >
              {isMenuOpen ? <X className="text-cyan-400" /> : <Menu className="text-cyan-400" />}
            </button>
          </div>

          {isMenuOpen && (
            <div className="md:hidden mt-4 pb-4 retro-terminal">
              <div className="flex flex-col space-y-4">
                <MobileNavLink href="#over-mij" onClick={() => setIsMenuOpen(false)}>OVER MIJ</MobileNavLink>
                <MobileNavLink href="#features" onClick={() => setIsMenuOpen(false)}>FEATURES</MobileNavLink>
                <MobileNavLink href="#demo" onClick={() => setIsMenuOpen(false)}>DEMO</MobileNavLink>
                <MobileNavLink href="#contact" onClick={() => setIsMenuOpen(false)}>CONTACT</MobileNavLink>
              </div>
            </div>
          )}
        </div>
      </nav>

      <section id="home" className="relative min-h-screen flex items-center">
        <div className="absolute inset-0 z-0">
          <spline-viewer url="https://prod.spline.design/GnlyDNjjtU-c7x37/scene.splinecode"></spline-viewer>
          <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/50 to-transparent"></div>
        </div>
        
        <div className="container mx-auto px-4 relative z-20 py-32">
          <div className="max-w-5xl mx-auto text-center">
            <h1 className="relative flex flex-col items-center justify-center mb-12 h-[6rem] md:h-[7rem] lg:h-[8rem]">
              <AnimatePresence mode="wait">
                <motion.span
                  key={titleIndex}
                  className="text-[4rem] md:text-[5rem] lg:text-[6rem] absolute text-[#4a9eff]"
                  initial={{ opacity: 0, y: 50 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -50 }}
                  transition={{ 
                    duration: 0.6,
                    ease: [0.43, 0.13, 0.23, 0.96]
                  }}
                >
                  {titles[titleIndex]}
                </motion.span>
              </AnimatePresence>
            </h1>
            <div className="text-[3rem] md:text-[3.5rem] lg:text-[4rem] font-bold text-red-500 mb-16 text-glow-red retro-terminal">
              Gegarandeerd
            </div>
            <div className="flex flex-col sm:flex-row justify-center gap-8">
              <a href="#demo" className="retro-button text-xl md:text-2xl px-10 py-5 hover:bg-cyan-400/20">
                BEKIJK DEMO &gt;
              </a>
              <a href="#contact" className="retro-button text-xl md:text-2xl px-10 py-5 hover:bg-cyan-400/20">
                NEEM CONTACT OP &gt;
              </a>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 retro-section">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl lg:text-6xl retro-terminal mb-4">Waarom kiezen voor</h2>
            <div className="text-5xl md:text-6xl lg:text-7xl font-bold text-[#4169E1] retro-terminal text-glow">AutoMate???</div>
          </div>
          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            <SlideIn delay={0}>
              <motion.div 
                className="retro-card p-8 rounded text-center min-h-[360px] flex flex-col items-center justify-between"
                whileHover={{ 
                  scale: 1.05,
                  boxShadow: "0 8px 16px rgba(74, 158, 255, 0.2)"
                }}
                transition={{ duration: 0.3 }}
              >
                <Shield className="w-16 h-16 text-cyan-400 mb-6" />
                <div className="w-full">
                  <h3 className="text-2xl md:text-3xl retro-terminal mb-4">Garantie</h3>
                  <p className="text-cyan-300 retro-terminal leading-relaxed">
                    Wij winnen alleen wanneer jij succesvol bent met jouw bedrijf.
                  </p>
                </div>
              </motion.div>
            </SlideIn>
            <SlideIn delay={200}>
              <motion.div 
                className="retro-card p-8 rounded text-center min-h-[360px] flex flex-col items-center justify-between"
                whileHover={{ 
                  scale: 1.05,
                  boxShadow: "0 8px 16px rgba(74, 158, 255, 0.2)"
                }}
                transition={{ duration: 0.3 }}
              >
                <MapPin className="w-16 h-16 text-cyan-400 mb-6" />
                <div className="w-full">
                  <h3 className="text-2xl md:text-3xl retro-terminal mb-4">LOKAAL</h3>
                  <p className="text-cyan-300 retro-terminal leading-relaxed">
                    We zijn niet weggestopt in een anoniem callcenter. We zijn een lokaal bedrijf dat altijd bereikbaar is.
                  </p>
                </div>
              </motion.div>
            </SlideIn>
            <SlideIn delay={400}>
              <motion.div 
                className="retro-card p-8 rounded text-center min-h-[360px] flex flex-col items-center justify-between"
                whileHover={{ 
                  scale: 1.05,
                  boxShadow: "0 8px 16px rgba(74, 158, 255, 0.2)"
                }}
                transition={{ duration: 0.3 }}
              >
                <Target className="w-16 h-16 text-cyan-400 mb-6" />
                <div className="w-full">
                  <h3 className="text-2xl md:text-3xl retro-terminal mb-4">Resultaat</h3>
                  <p className="text-cyan-300 retro-terminal leading-relaxed">
                    Onze eerste prioriteit is om resultaten voor jou te behalen. Minder praten, meer doen.
                  </p>
                </div>
              </motion.div>
            </SlideIn>
          </div>
        </div>
      </section>

      <section id="features" className="py-20 retro-section">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl md:text-5xl lg:text-6xl retro-terminal text-center mb-12">SYSTEEM FEATURES</h2>
          <div className="grid md:grid-cols-2 gap-8 max-w-6xl mx-auto">
            <SlideIn delay={0}>
              <FeatureCard
                icon={<Globe className="w-12 h-12" />}
                title="MAATWERK WEBSITES"
                description="PROFESSIONELE WEBSITES DIE JE BEDRIJF BETER VINDBAAR MAKEN EN MEER LEADS GENEREREN"
              />
            </SlideIn>
            <SlideIn delay={200}>
              <FeatureCard
                icon={<Bot className="w-12 h-12" />}
                title="AI-GESTUURDE CHATBOTS"
                description="24/7 AUTOMATISCHE KLANTINTERACTIE EN COMMUNICATIE VIA SLIMME AI-CHATBOTS"
              />
            </SlideIn>
            <SlideIn delay={400}>
              <FeatureCard
                icon={<Users className="w-12 h-12" />}
                title="VERBETERDE KLANTBELEVING"
                description="SNELLERE EN EFFICIËNTERE SERVICE, WAT LEIDT TOT MEER TEVREDEN KLANTEN EN HERHALINGSAANKOPEN"
              />
            </SlideIn>
            <SlideIn delay={600}>
              <FeatureCard
                icon={<TrendingUp className="w-12 h-12" />}
                title="MEER KLANTEN & OMZET"
                description="SLIMME AUTOMATISERING OM MEER KLANTEN AAN TE TREKKEN EN CONVERSIES TE VERHOGEN"
              />
            </SlideIn>
          </div>
        </div>
      </section>

      <section id="demo" className="py-20 retro-section retro-gradient">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl md:text-5xl lg:text-6xl retro-terminal text-center mb-12">BEKIJK DE DEMO</h2>
          <div className="max-w-4xl mx-auto">
            <SlideIn>
              <div className="retro-card p-6 rounded">
                <div className="space-y-8">
                  <div className="border-b border-cyan-400/20 pb-6">
                    <h3 className="text-2xl md:text-3xl retro-terminal mb-4 flex items-center">
                      <Calendar className="w-6 h-6 mr-2" />
                      🍽️ WhatsApp Reserveringsbot Gesprek
                    </h3>
                    <div className="flex flex-col space-y-4">
                      {activeDemo === 'booking' && reservationConversation.slice(0, Math.min(conversationStep, reservationConversation.length)).map((msg, idx) => (
                        <Message key={idx} text={msg.text} isAi={msg.isAi} />
                      ))}
                    </div>
                  </div>

                  <div>
                    <h3 className="text-2xl md:text-3xl retro-terminal mb-4 flex items-center">
                      <Wine className="w-6 h-6 mr-2" />
                      🍷 Upsell: Wijn Bestellen vooraf
                    </h3>
                    <div className="flex flex-col space-y-4">
                      {activeDemo === 'booking' && conversationStep > reservationConversation.length && 
                        wineConversation.slice(0, conversationStep - reservationConversation.length).map((msg, idx) => (
                          <Message key={idx} text={msg.text} isAi={msg.isAi} />
                        ))
                      }
                    </div>
                  </div>

                  {!activeDemo && (
                    <div className="flex justify-center">
                      <button 
                        className="retro-button px-6 py-3"
                        onClick={() => {
                          setActiveDemo('booking');
                          setConversationStep(0);
                        }}
                      >
                        START DEMO &gt;
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </SlideIn>
          </div>
        </div>
      </section>

      <section id="over-mij" className="py-20 retro-section">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl md:text-5xl lg:text-6xl retro-terminal text-center mb-12">OVER MIJ - JASON KNUBBEN</h2>
          <div className="max-w-4xl mx-auto">
            <SlideIn>
              <div className="retro-card p-8 rounded">
                <div className="flex flex-col md:flex-row gap-8 items-center">
                  <div className="flex-shrink-0">
                    <User className="w-24 h-24 text-cyan-400" />
                  </div>
                  <div className="space-y-4">
                    <p className="text-cyan-300 retro-terminal">
                      Ik ben Jason Knubben, eigenaar van AutoMate en specialist in digitale automatisering voor ondernemers. 
                      Ik ontwikkel op maat gemaakte websites en AI-gestuurde chatbots die de klantervaring verbeteren en meer omzet genereren.
                    </p>
                    <p className="text-cyan-300 retro-terminal">
                      Daarnaast werk ik als IT-specialist bij Vodafone en heb ik een passie voor AI en innovatie. 
                      Mijn missie is om ondernemers te helpen groeien met slimme digitale oplossingen die hun bedrijf naar het volgende niveau tillen.
                    </p>
                    <div className="pt-4">
                      <a href="#contact" className="retro-button px-6 py-3 inline-block">
                        BENIEUWD HOE DIT JOUW BEDRIJF KAN HELPEN? LATEN WE PRATEN! &gt;
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </SlideIn>
          </div>
        </div>
      </section>

      <section id="contact" className="py-20 retro-section">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl md:text-5xl lg:text-6xl retro-terminal text-center mb-12">NEEM CONTACT OP</h2>
          <div className="grid md:grid-cols-2 gap-12 max-w-4xl mx-auto">
            <SlideIn delay={0}>
              <div className="space-y-6 retro-terminal">
                <ContactInfo icon={<Mail />} text="JASON@JKAUTOMATE.COM" />
                <ContactInfo icon={<Building2 />} text="KERKRADE, NEDERLAND" />
              </div>
            </SlideIn>
            <SlideIn delay={200}>
              <form className="space-y-6" onSubmit={handleSubmit}>
                <input
                  type="text"
                  name="from_name"
                  placeholder="JE_NAAM"
                  className="w-full px-4 py-3 retro-input rounded"
                  required
                />
                <input
                  type="email"
                  name="from_email"
                  placeholder="JE_EMAIL"
                  className="w-full px-4 py-3 retro-input rounded"
                  required
                />
                <textarea
                  name="message"
                  placeholder="JE_BERICHT"
                  rows={4}
                  className="w-full px-4 py-3 retro-input rounded"
                  required
                />
                <button
                  type="submit"
                  className="w-full retro-button py-3"
                  disabled={formStatus === 'sending'}
                >
                  {formStatus === 'sending' ? 'VERSTUREN...' : 
                   formStatus === 'success' ? 'VERSTUURD!' :
                   formStatus === 'error' ? 'FOUT! PROBEER OPNIEUW' :
                   'VERSTUREN >'}
                </button>
              </form>
            </SlideIn>
          </div>
        </div>
      </section>

      <footer className="py-12 retro-section">
        <div className="container mx-auto px-4">
          <div className="text-center retro-terminal">
            <div className="flex justify-center items-center space-x-2 mb-4">
              <Logo className="w-6 h-6 text-cyan-400" />
              <span className="text-lg">AUTOMATE</span>
            </div>
            <p className="text-sm text-cyan-300">
              © {new Date().getFullYear()} AUTOMATE_SYSTEMS
              <br />
              ALLE_RECHTEN_VOORBEHOUDEN
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}

function NavLink({ href, children }: { href: string; children: React.ReactNode }) {
  return (
    <a href={href} className="hover:text-cyan-300 transition">
      {children}
    </a>
  );
}

function MobileNavLink({ href, children, onClick }: { href: string; children: React.ReactNode; onClick: () => void }) {
  return (
    <a href={href} className="hover:text-cyan-300 transition block" onClick={onClick}>
      {children}
    </a>
  );
}

function Message({ text, isAi = false }: { text: string; isAi?: boolean }) {
  return (
    <div className={`flex ${isAi ? 'justify-start' : 'justify-end'}`}>
      <div className={`max-w-[80%] p-4 rounded ${
        isAi ? 'bg-cyan-950/30 border border-cyan-400/20' : 'bg-cyan-400/10'
      }`}>
        <p className="retro-terminal text-sm whitespace-pre-line">{text}</p>
      </div>
    </div>
  );
}

function FeatureCard({ icon, title, description }: { icon: React.ReactNode; title: string; description: string }) {
  return (
    <motion.div 
      className="retro-card p-8 rounded h-full"
      whileHover={{ 
        scale: 1.05,
        boxShadow: "0 8px 16px rgba(74, 158, 255, 0.2)"
      }}
      transition={{ duration: 0.3 }}
    >
      <div className="text-cyan-400 mb-6">{icon}</div>
      <h3 className="text-2xl md:text-3xl retro-terminal mb-4">{title}</h3>
      <p className="text-cyan-300 retro-terminal text-sm leading-relaxed">{description}</p>
    </motion.div>
  );
}

function ContactInfo({ icon, text }: { icon: React.ReactNode; text: string }) {
  return (
    <div className="flex items-center space-x-3">
      <div className="text-cyan-400">{icon}</div>
      <span>{text}</span>
    </div>
  );
}

export default App;